import React, { useRef, useState } from "react";
import { Modal, Form, Alert, ProgressBar } from "react-bootstrap";
import { Button } from "../../Common";
import { bulkPlanMigration } from "../../api";
import styles from "./Customer.module.css";

const BulkPlanMigration = ({ show, onClose }) => {
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [progress, setProgress] = useState(0);

    const resetState = () => {
        setFile(null);
        setUploading(false);
        setStatusMessage("");
        setErrorMessage("");
        setProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClose = () => {
        if (uploading) return;
        resetState();
        onClose();
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setErrorMessage("");
        setStatusMessage("");
        setProgress(0);
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setErrorMessage("Please select a CSV file to upload.");
            return;
        }
        setUploading(true);
        setErrorMessage("");
        setStatusMessage("");
        setProgress(10);

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await bulkPlanMigration(formData);
            setProgress(80);
            if (response && response.status == 200) {
                setStatusMessage(response?.data?.message || "Bulk plan migration file uploaded successfully. Processing will begin shortly. You will receive an email once the migration is completed.");
            } else {
                const msg = response?.data?.message || "Bulk plan migration failed. Please check the file.";
                setErrorMessage(msg);
            }
        } catch (error) {
            const msg = error?.response?.data?.message || "Something went wrong while uploading the file.";
            setErrorMessage(msg);
        } finally {
            setProgress(100);
            setUploading(false);
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setTimeout(() => {
                setProgress(0);
            }, 500);
        }
    };

    const handleDownloadSampleCsv = () => {
        const csv =
            "UserName,NewPlanCode,NewRenewalDate,Comment\n" +
            "sample.user,NEW_PLAN_CODE,18/06/2026,Comment\n";

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "BulkPlanMigrationTemplate.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
            keyboard={!uploading}
        >
            <Modal.Header className={styles.bulkPlanMigrationHeader}>
                <div className={styles.bulkPlanMigrationHeaderRow}>
                    <Modal.Title className={styles.bulkPlanMigrationTitle}>
                        Bulk Plan Migration
                    </Modal.Title>
                    {!uploading && (
                        <span
                            onClick={handleClose}
                            className={styles.bulkPlanMigrationClose}
                        >
                            &times;
                        </span>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body className={styles.bulkPlanMigrationBody}>
                <div className={styles.bulkPlanMigrationInstruction}>
                    <div className={styles.bulkPlanMigrationInstructionHeader}>
                        <Button
                            onClick={handleDownloadSampleCsv}
                            title={"Download Sample CSV"}
                            disabled={uploading}
                            classes={styles.bulkPlanMigrationTemplateButton}
                        />
                    </div>
                    <div className={styles.bulkPlanMigrationInstructionHint}>
                        NewRenewalDate must be in{" "}
                        <span className={styles.bulkPlanMigrationMonospace}>dd/mm/yyyy</span>{" "}
                        format. Example:{" "}
                        <span className={styles.bulkPlanMigrationMonospace}>
                            18/06/2026
                        </span>
                        .
                    </div>
                </div>

                <div className={styles.bulkPlanMigrationFileBlock}>
                    <Form.Label className={styles.bulkPlanMigrationFileLabel}>Select CSV File</Form.Label>
                    <Form.Control
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        disabled={uploading}
                        size="sm"
                        className={styles.bulkPlanMigrationFileInput}
                    />
                </div>

                {uploading && (
                    <div className={styles.bulkPlanMigrationUploading}>
                        <div className={styles.bulkPlanMigrationUploadingMsg}>
                            Upload in progress... Do not close the browser.
                        </div>
                        <ProgressBar
                            now={progress}
                            label={`${progress}%`}
                            animated
                            variant="success"
                            className={styles.bulkPlanMigrationProgress}
                        />
                    </div>
                )}

                {statusMessage && (
                    <Alert variant="success" className={styles.bulkPlanMigrationAlert}>
                        {statusMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert variant="danger" className={styles.bulkPlanMigrationAlert}>
                        {errorMessage}
                    </Alert>
                )}

            </Modal.Body>
            <Modal.Footer className={styles.bulkPlanMigrationFooter}>
                <Button
                    onClick={handleUpload}
                    title={uploading ? "Uploading..." : "Upload"}
                    disabled={uploading || !file}
                    loading={uploading}
                />
                <Button
                    onClick={handleClose}
                    title="Close"
                    disabled={uploading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default BulkPlanMigration;